import React, { useState, useEffect } from 'react';
import './DictionaryTable.css';
import { canonicalTermApi } from '../../../services/apis/configuredApis';

const TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'mlcf:domain:', label: 'Domain Vocabulary' },
  { value: 'mlcf:context:', label: 'Context Categories' },
  { value: 'other', label: 'Other (non-MLCF)' },
];

function CanonicalTermTable({ onEdit, graphId }) {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [filteredTerms, setFilteredTerms] = useState([]);

  const fetchTerms = async (search = null) => {
    setLoading(true);
    setError(null);
    try {
      const response = await canonicalTermApi.canonicalTermListGet({ search, limit: 500, offset: 0 });
      const responseData = response?.data || response;
      const termsList = Array.isArray(responseData) ? responseData : (responseData?.terms || []);
      setTerms(termsList);
    } catch (err) {
      console.error('Error fetching canonical terms:', err);
      setError(err.response?.data?.message || err.message || 'Failed to fetch canonical terms');
      setTerms([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (typeFilter === 'mlcf:domain:' || typeFilter === 'mlcf:context:') {
      fetchTerms(typeFilter);
    } else {
      fetchTerms();
    }
  }, [graphId, typeFilter]);

  useEffect(() => {
    const searchTimeout = setTimeout(() => {
      let working = [...terms];

      if (typeFilter === 'other') {
        working = working.filter(t =>
          !t.code?.startsWith('mlcf:domain:') && !t.code?.startsWith('mlcf:context:')
        );
      }

      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase();
        working = working.filter(t =>
          t.code?.toLowerCase().includes(term) ||
          t.display?.toLowerCase().includes(term) ||
          t.description?.toLowerCase().includes(term)
        );
      }

      setFilteredTerms(working);
    }, 300);

    return () => clearTimeout(searchTimeout);
  }, [searchTerm, typeFilter, terms]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (term) => {
    if (window.confirm(`Are you sure you want to delete "${term.display}"?`)) {
      try {
        await canonicalTermApi.canonicalTermDeleteTermIdDelete(term.id);
        // Refresh the list after deletion
        await fetchTerms(searchTerm || null);
      } catch (err) {
        console.error('Error deleting canonical term:', err);
        // Extract error message from response
        const errorMessage = err.response?.data?.error || 
                           err.response?.data?.message || 
                           err.message || 
                           'Failed to delete canonical term. Please try again.';
        
        // Check if it's a reference error
        if (errorMessage.includes('referenced by dictionary entries')) {
          alert(`Cannot delete "${term.display}" because it is currently being used by one or more dictionary entries. Please delete or update those dictionary entries first.`);
        } else {
          alert(`Failed to delete canonical term: ${errorMessage}`);
        }
      }
    }
  };

  if (loading && terms.length === 0) {
    return <div className="dictionary-loading">Loading canonical terms...</div>;
  }

  if (error && terms.length === 0) {
    return (
      <div className="dictionary-table-container">
        <div className="dictionary-empty">
          <div className="dictionary-empty-icon">⚠️</div>
          <h3>Error Loading Canonical Terms</h3>
          <p>{error}</p>
          <button
            onClick={() => fetchTerms()}
            style={{
              marginTop: '1rem',
              padding: '0.5rem 1rem',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dictionary-table-container">
      <div className="dictionary-table-controls">
        <input
          type="text"
          className="dictionary-search"
          placeholder="Search canonical terms..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select
          className="dictionary-filter"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {TYPE_FILTERS.map(f => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </select>
      </div>

      {filteredTerms.length === 0 ? (
        <div className="dictionary-empty">
          <div className="dictionary-empty-icon">📚</div>
          <h3>No Canonical Terms Found</h3>
          <p>
            {searchTerm ? 'No terms match your search.' : 'Get started by creating your first canonical term.'}
          </p>
        </div>
      ) : (
        <div className="dictionary-table-wrapper">
          <table className="dictionary-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Source</th>
                <th>Display</th>
                <th>Description</th>
                <th>Node ID</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTerms.map((term) => {
                const isDomain = term.code?.startsWith('mlcf:domain:');
                const isContext = term.code?.startsWith('mlcf:context:');
                const isAutoExtracted = isDomain || isContext;
                return (
                <tr key={term.id}>
                  <td>
                    <strong className="dictionary-code">{term.code}</strong>
                  </td>
                  <td>
                    {isDomain && (
                      <span className="dictionary-badge" style={{ background: '#dbeafe', color: '#1e40af', fontSize: '0.75em' }}>
                        Domain
                      </span>
                    )}
                    {isContext && (
                      <span className="dictionary-badge" style={{ background: '#fef3c7', color: '#92400e', fontSize: '0.75em' }}>
                        Context
                      </span>
                    )}
                    {!isAutoExtracted && (
                      <span className="dictionary-badge" style={{ background: '#f3f4f6', color: '#6b7280', fontSize: '0.75em' }}>
                        Manual
                      </span>
                    )}
                  </td>
                  <td>{term.display}</td>
                  <td className="dictionary-description">
                    {term.description || '—'}
                  </td>
                  <td className="dictionary-meta">
                    {term.node_id ? (
                      <span className="dictionary-badge dictionary-badge-info">
                        {term.node_id.substring(0, 8)}...
                      </span>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>
                    <span
                      className={`dictionary-status ${
                        term.is_active
                          ? 'dictionary-status-active'
                          : 'dictionary-status-inactive'
                      }`}
                    >
                      {term.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="dictionary-meta">{formatDate(term.created_at)}</td>
                  <td className="dictionary-meta">{formatDate(term.updated_at)}</td>
                  <td>
                    <div className="dictionary-actions">
                      <button
                        className="dictionary-action-btn dictionary-action-btn-edit"
                        onClick={() => onEdit(term)}
                        title="Edit Term"
                      >
                        ✏️
                      </button>
                      <button
                        className="dictionary-action-btn dictionary-action-btn-delete"
                        onClick={() => handleDelete(term)}
                        title="Delete Term"
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default CanonicalTermTable;

